import type { Random } from '../../core/Random';
import { DefaultRandom } from '../../core/Random';
import type { BingoCard } from './types';

/** Per-column number ranges (US bingo). */
const COLUMN_RANGES: ReadonlyArray<readonly [number, number]> = [
  [1, 15],
  [16, 30],
  [31, 45],
  [46, 60],
  [61, 75],
];

/**
 * Generate a fresh bingo card: 5 numbers per column (4 for the centre column,
 * since the centre cell is FREE), no duplicates within a column, drawn
 * uniformly from the column's range.
 *
 * <p>Pure given a {@link Random} — pass {@code PredeterminedRandom} for tests.
 */
export function generateBingoCard(random: Random = new DefaultRandom()): BingoCard {
  const cells: number[][] = [];
  for (let col = 0; col < 5; col++) {
    const range = COLUMN_RANGES[col]!;
    const min = range[0];
    const max = range[1];
    const pool: number[] = [];
    for (let n = min; n <= max; n++) pool.push(n);
    const column: number[] = [];
    for (let row = 0; row < 5; row++) {
      if (col === 2 && row === 2) {
        column.push(0); // FREE square
        continue;
      }
      const idx = random.nextIntBetween(0, pool.length);
      column.push(pool.splice(idx, 1)[0]!);
    }
    cells.push(column);
  }
  return { cells };
}

/** Determine the column-letter for a called number (1–75). */
export function columnLetterFor(num: number): 'B' | 'I' | 'N' | 'G' | 'O' {
  if (num <= 15) return 'B';
  if (num <= 30) return 'I';
  if (num <= 45) return 'N';
  if (num <= 60) return 'G';
  return 'O';
}
